/**
 * PMO Radiator Dashboard — burnup/burndown/velocity charts for portal development progress
 * Two view modes: Executive (management) and Detailed (Yousef)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell,
  ReferenceLine, ComposedChart
} from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const COLORS = ['#0d6efd', '#198754', '#ffc107', '#dc3545', '#6f42c1', '#0dcaf0', '#fd7e14', '#20c997', '#6610f2', '#d63384'];
const STATUS_COLORS = { completed: '#198754', in_progress: '#0d6efd', planned: '#adb5bd', deferred: '#ffc107', cut: '#dc3545' };
const STATUS_LABELS = { completed: 'Completed', in_progress: 'In Progress', planned: 'Planned', deferred: 'Deferred', cut: 'Cut' };

const PMORadiator = () => {
  const { instance, accounts } = useMsal();
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('executive'); // 'executive' | 'detailed'
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', module: '', story_points: 3, status: 'planned', is_baseline: false });
  const dashboardRef = useRef(null);

  const syncHeaders = useCallback(() => ({ 'x-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone }), []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [sumRes, itemRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', '/pmo-radiator/summary', null, syncHeaders()),
        authenticatedApiCall(instance, accounts, 'GET', '/pmo-radiator/items', null, syncHeaders()),
      ]);
      if (sumRes.data.success) setSummary(sumRes.data.data);
      if (itemRes.data.success) setItems(itemRes.data.data);
    } catch (err) {
      console.error('Failed to load PMO radiator data:', err);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, syncHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSeed = async () => {
    try {
      await authenticatedApiCall(instance, accounts, 'POST', '/pmo-radiator/seed', {}, syncHeaders());
      fetchData();
    } catch (err) {
      console.error('Seed error:', err);
    }
  };

  const handleSaveEdit = async (id) => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/pmo-radiator/items/${id}`, editForm, syncHeaders());
      setEditingId(null);
      setEditForm({});
      fetchData();
    } catch (err) {
      console.error('Update error:', err);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.title || !newItem.module) return;
    try {
      await authenticatedApiCall(instance, accounts, 'POST', '/pmo-radiator/items', newItem, syncHeaders());
      setShowAddForm(false);
      setNewItem({ title: '', module: '', story_points: 3, status: 'planned', is_baseline: false });
      fetchData();
    } catch (err) {
      console.error('Add error:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/pmo-radiator/items/${id}`, null, syncHeaders());
      fetchData();
    } catch (err) {
      console.error('Delete error:', err);
    }
  };

  const handleExportPDF = async () => {
    if (!dashboardRef.current) return;
    try {
      const canvas = await html2canvas(dashboardRef.current, { scale: 2, useCORS: true, backgroundColor: '#fff' });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4');
      const pdfW = pdf.internal.pageSize.getWidth();
      const pdfH = pdf.internal.pageSize.getHeight();
      const ratio = Math.min(pdfW / canvas.width, pdfH / canvas.height);
      const w = canvas.width * ratio;
      const h = canvas.height * ratio;
      pdf.addImage(imgData, 'PNG', (pdfW - w) / 2, 5, w, h);
      pdf.save(`PMO-Radiator-${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
    }
  };

  const fmtMonth = (m) => {
    const [y, mo] = m.split('-');
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${names[parseInt(mo, 10) - 1]} ${y.slice(2)}`;
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', color: '#6b7280' }}>
        Loading PMO Radiator...
      </div>
    );
  }

  if (!summary || !summary.burnup || summary.burnup.length === 0) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2 style={{ marginBottom: '1rem', color: '#374151' }}>PMO Radiator</h2>
        <p style={{ color: '#6b7280', marginBottom: '1rem' }}>No data yet. Seed the initial roadmap items to get started.</p>
        <button onClick={handleSeed} style={{ padding: '10px 24px', backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>
          Seed Roadmap Data
        </button>
      </div>
    );
  }

  const { totalSP, completedSP, baselineSP, inProgressSP, percentComplete, burnup, velocity, modules } = summary;
  const remainingSP = totalSP - completedSP;
  const addedScope = totalSP - baselineSP;

  // Pie data for scope breakdown
  const scopePie = [
    { name: 'Baseline (Done)', value: Math.min(baselineSP, completedSP) },
    { name: 'Added (Done)', value: Math.max(0, completedSP - baselineSP) },
    { name: 'In Progress', value: inProgressSP },
    { name: 'Remaining', value: remainingSP - inProgressSP },
  ].filter(d => d.value > 0);
  const pieColors = ['#198754', '#0dcaf0', '#0d6efd', '#dee2e6'];

  const cardStyle = { backgroundColor: '#fff', borderRadius: '8px', padding: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' };
  const labelStyle = { fontSize: '11px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' };
  const valueStyle = { fontSize: '28px', fontWeight: '700', color: '#111827' };

  return (
    <div style={{ padding: '0' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.4rem', color: '#111827' }}>PMO Radiator</h2>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '13px' }}>
            Portal development progress — baseline Sep 2025
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ display: 'flex', borderRadius: '6px', overflow: 'hidden', border: '1px solid #d1d5db' }}>
            <button
              onClick={() => setViewMode('executive')}
              style={{
                padding: '6px 16px', fontSize: '12px', border: 'none', cursor: 'pointer',
                backgroundColor: viewMode === 'executive' ? '#374151' : '#f9fafb',
                color: viewMode === 'executive' ? '#fff' : '#374151',
                fontWeight: viewMode === 'executive' ? '600' : '400',
              }}
            >Executive</button>
            <button
              onClick={() => setViewMode('detailed')}
              style={{
                padding: '6px 16px', fontSize: '12px', border: 'none', cursor: 'pointer',
                backgroundColor: viewMode === 'detailed' ? '#374151' : '#f9fafb',
                color: viewMode === 'detailed' ? '#fff' : '#374151',
                fontWeight: viewMode === 'detailed' ? '600' : '400',
              }}
            >Detailed</button>
          </div>
          <button onClick={handleExportPDF} style={{ padding: '6px 14px', fontSize: '12px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', color: '#374151' }}>
            Export PDF
          </button>
        </div>
      </div>

      <div ref={dashboardRef}>
        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={cardStyle}>
            <div style={labelStyle}>Total Scope</div>
            <div style={valueStyle}>{totalSP} <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400' }}>SP</span></div>
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>Baseline: {baselineSP} | Added: {addedScope}</div>
          </div>
          <div style={cardStyle}>
            <div style={labelStyle}>Completed</div>
            <div style={{ ...valueStyle, color: '#198754' }}>{completedSP} <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400' }}>SP</span></div>
          </div>
          <div style={cardStyle}>
            <div style={labelStyle}>In Progress</div>
            <div style={{ ...valueStyle, color: '#0d6efd' }}>{inProgressSP} <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400' }}>SP</span></div>
          </div>
          <div style={cardStyle}>
            <div style={labelStyle}>Remaining</div>
            <div style={{ ...valueStyle, color: '#dc3545' }}>{remainingSP} <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: '400' }}>SP</span></div>
          </div>
          <div style={cardStyle}>
            <div style={labelStyle}>Progress</div>
            <div style={valueStyle}>{percentComplete}%</div>
            <div style={{ marginTop: '6px', height: '6px', backgroundColor: '#e5e7eb', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ width: `${percentComplete}%`, height: '100%', backgroundColor: '#198754', borderRadius: '3px', transition: 'width 0.5s' }} />
            </div>
          </div>
        </div>

        {/* Charts Row 1: Burnup + Scope Pie */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={cardStyle}>
            <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '12px', color: '#111827' }}>Burnup Chart</div>
            <ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={burnup}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tickFormatter={fmtMonth} fontSize={11} />
                <YAxis fontSize={11} label={{ value: 'Story Points', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
                <Tooltip labelFormatter={fmtMonth} />
                <Legend />
                <ReferenceLine y={baselineSP} stroke="#ffc107" strokeDasharray="5 5" label={{ value: 'Baseline', position: 'right', fontSize: 10 }} />
                <Area type="monotone" dataKey="scope" name="Total Scope" stroke="#6b7280" fill="#f3f4f6" strokeWidth={2} />
                <Area type="monotone" dataKey="completed" name="Completed" stroke="#198754" fill="#dcfce7" strokeWidth={2} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div style={cardStyle}>
            <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '12px', color: '#111827' }}>Scope Breakdown</div>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={scopePie} cx="50%" cy="50%" innerRadius={60} outerRadius={95} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={true} fontSize={10}>
                  {scopePie.map((_, idx) => <Cell key={idx} fill={pieColors[idx % pieColors.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Charts Row 2: Velocity + Burndown (detailed only shows burndown) */}
        {viewMode === 'detailed' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div style={cardStyle}>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '12px', color: '#111827' }}>Monthly Velocity</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={velocity}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tickFormatter={fmtMonth} fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip labelFormatter={fmtMonth} />
                  <Bar dataKey="sp" name="Story Points" fill="#0d6efd" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={cardStyle}>
              <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '12px', color: '#111827' }}>Burndown</div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={burnup.map(b => ({ month: b.month, remaining: b.scope - b.completed }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="month" tickFormatter={fmtMonth} fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip labelFormatter={fmtMonth} />
                  <Line type="monotone" dataKey="remaining" name="Remaining SP" stroke="#dc3545" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Module Progress Table */}
        <div style={cardStyle}>
          <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '12px', color: '#111827' }}>Module Progress</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: '600' }}>Module</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', color: '#6b7280', fontWeight: '600' }}>Total SP</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', color: '#6b7280', fontWeight: '600' }}>Done</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', color: '#6b7280', fontWeight: '600' }}>In Progress</th>
                <th style={{ textAlign: 'center', padding: '8px 12px', color: '#6b7280', fontWeight: '600' }}>Planned</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: '600', width: '30%' }}>Progress</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((m, idx) => {
                const pct = m.total > 0 ? Math.round((m.completed / m.total) * 100) : 0;
                return (
                  <tr key={m.name} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '8px 12px', fontWeight: '500' }}>
                      <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: COLORS[idx % COLORS.length], marginRight: '8px' }} />
                      {m.name}
                      {m.baseline > 0 && <span style={{ fontSize: '10px', color: '#ffc107', marginLeft: '6px' }}>★ baseline</span>}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 12px' }}>{m.total}</td>
                    <td style={{ textAlign: 'center', padding: '8px 12px', color: '#198754', fontWeight: '600' }}>{m.completed}</td>
                    <td style={{ textAlign: 'center', padding: '8px 12px', color: '#0d6efd' }}>{m.inProgress}</td>
                    <td style={{ textAlign: 'center', padding: '8px 12px', color: '#9ca3af' }}>{m.planned}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', backgroundColor: pct === 100 ? '#198754' : '#0d6efd', borderRadius: '4px' }} />
                        </div>
                        <span style={{ fontSize: '11px', color: '#6b7280', minWidth: '32px' }}>{pct}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detailed Mode: Full Item Table with editing */}
      {viewMode === 'detailed' && (
        <div style={{ ...cardStyle, marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>All Roadmap Items ({items.length})</div>
            <button onClick={() => setShowAddForm(!showAddForm)} style={{ padding: '5px 14px', fontSize: '12px', backgroundColor: '#198754', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
              {showAddForm ? 'Cancel' : '+ Add Item'}
            </button>
          </div>

          {showAddForm && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
              <input placeholder="Title" value={newItem.title} onChange={e => setNewItem(p => ({ ...p, title: e.target.value }))} style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', flex: '1 1 200px' }} />
              <input placeholder="Module" value={newItem.module} onChange={e => setNewItem(p => ({ ...p, module: e.target.value }))} style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', width: '140px' }} />
              <input type="number" placeholder="SP" value={newItem.story_points} onChange={e => setNewItem(p => ({ ...p, story_points: parseInt(e.target.value) || 1 }))} style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px', width: '60px' }} />
              <select value={newItem.status} onChange={e => setNewItem(p => ({ ...p, status: e.target.value }))} style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '4px' }}>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <button onClick={handleAddItem} style={{ padding: '5px 14px', fontSize: '12px', backgroundColor: '#0d6efd', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
            </div>
          )}

          <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280' }}>Title</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', color: '#6b7280' }}>Module</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', color: '#6b7280' }}>SP</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', color: '#6b7280' }}>Status</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', color: '#6b7280' }}>Actual Start</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', color: '#6b7280' }}>Actual End</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', color: '#6b7280' }}>Baseline</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px', color: '#6b7280' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const isEditing = editingId === item.id;
                  return (
                    <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ padding: '6px 8px' }}>
                        {isEditing
                          ? <input value={editForm.title ?? item.title} onChange={e => setEditForm(p => ({ ...p, title: e.target.value }))} style={{ width: '100%', padding: '3px 6px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '3px' }} />
                          : item.title}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        {isEditing
                          ? <input value={editForm.module ?? item.module} onChange={e => setEditForm(p => ({ ...p, module: e.target.value }))} style={{ width: '100%', padding: '3px 6px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '3px' }} />
                          : item.module}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                        {isEditing
                          ? <input type="number" value={editForm.story_points ?? item.story_points} onChange={e => setEditForm(p => ({ ...p, story_points: parseInt(e.target.value) || 1 }))} style={{ width: '50px', padding: '3px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '3px', textAlign: 'center' }} />
                          : item.story_points}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                        {isEditing
                          ? <select value={editForm.status ?? item.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))} style={{ padding: '3px', fontSize: '12px', border: '1px solid #d1d5db', borderRadius: '3px' }}>
                              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                          : <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', backgroundColor: STATUS_COLORS[item.status] + '20', color: STATUS_COLORS[item.status], fontWeight: '600' }}>{STATUS_LABELS[item.status]}</span>}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 8px', fontSize: '11px', color: '#6b7280' }}>
                        {isEditing
                          ? <input type="date" value={editForm.actual_start ?? (item.actual_start ? item.actual_start.slice(0, 10) : '')} onChange={e => setEditForm(p => ({ ...p, actual_start: e.target.value }))} style={{ padding: '2px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '3px' }} />
                          : (item.actual_start ? new Date(item.actual_start).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—')}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 8px', fontSize: '11px', color: '#6b7280' }}>
                        {isEditing
                          ? <input type="date" value={editForm.actual_end ?? (item.actual_end ? item.actual_end.slice(0, 10) : '')} onChange={e => setEditForm(p => ({ ...p, actual_end: e.target.value }))} style={{ padding: '2px', fontSize: '11px', border: '1px solid #d1d5db', borderRadius: '3px' }} />
                          : (item.actual_end ? new Date(item.actual_end).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }) : '—')}
                      </td>
                      <td style={{ textAlign: 'center', padding: '6px 8px' }}>{item.is_baseline ? '★' : ''}</td>
                      <td style={{ textAlign: 'center', padding: '6px 8px' }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button onClick={() => handleSaveEdit(item.id)} style={{ padding: '2px 8px', fontSize: '11px', backgroundColor: '#198754', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Save</button>
                            <button onClick={() => { setEditingId(null); setEditForm({}); }} style={{ padding: '2px 8px', fontSize: '11px', backgroundColor: '#6b7280', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}>Cancel</button>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                            <button onClick={() => { setEditingId(item.id); setEditForm({}); }} style={{ padding: '2px 8px', fontSize: '11px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: '3px', cursor: 'pointer' }}>Edit</button>
                            <button onClick={() => handleDelete(item.id)} style={{ padding: '2px 8px', fontSize: '11px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '3px', cursor: 'pointer', color: '#dc3545' }}>Del</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PMORadiator;
